'use client';

import NextLink from 'next/link';
import { usePathname } from 'next/navigation';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { pathForNextNavigation } from '@/lib/appBasePath';
import { getVoiceSectionForTabBar } from '@/lib/voiceSections';

// ----------------------------------------------------------------------

type Props = {
  hasPermission: (permission: string) => boolean;
  /** Pending-callback count shown on the Callbacks tab. */
  callbackBadge?: number;
};

/**
 * Tab bar for the Voice sections (Agents, Campaigns, Calls, Contacts, Settings).
 * Each tab is its own route, so the old pages keep their URLs. Renders nothing off a
 * tab's own list page (create/detail sub-pages are full-screen flows) or when the
 * user can see fewer than two tabs.
 */
export function VoiceSectionTabs({ hasPermission, callbackBadge = 0 }: Props) {
  const pathname = pathForNextNavigation(usePathname() || '');
  const section = getVoiceSectionForTabBar(pathname);
  if (!section) return null;

  const tabs = section.tabs.filter((t) => hasPermission(t.permission));
  // Pages in a section with a shared header have dropped their own title, so keep the header even with one tab.
  if (tabs.length < 2 && !section.header) return null;

  const current = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const value =
    tabs.find((t) => t.path === current || t.aliasPaths?.includes(current))
      ?.path ?? false;

  const tabBar =
    tabs.length < 2 ? null : (
      <Tabs
        value={value}
        variant="scrollable"
        scrollButtons={false}
        aria-label={`${section.title} tabs`}
        sx={{
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.path}
            component={NextLink}
            href={t.path}
            value={t.path}
            label={
              t.path === '/voice-callbacks' && callbackBadge > 0 ? (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
                >
                  {t.label}
                  <Box
                    component="span"
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.75,
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'var(--teal-300, #58C3BE)',
                      color: '#0D2430',
                    }}
                  >
                    {callbackBadge}
                  </Box>
                </Box>
              ) : (
                t.label
              )
            }
            sx={{
              minHeight: 40,
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 500,
            }}
          />
        ))}
      </Tabs>
    );

  if (!section.header) return tabBar;
  return (
    // Inset matches the Calls pages' own content padding so the header lines up with what's below it.
    <Box sx={{ px: 2.5, pt: 2.5 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{ fontWeight: 700, letterSpacing: '-0.015em' }}
        >
          {section.header.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.75 }}>
          {section.header.subtitle}
        </Typography>
      </Box>
      {tabBar}
    </Box>
  );
}
